/*
 * Free shipping progress module.
 *
 * All of the maths lives in Liquid. In the cart drawer the whole mini-cart section is re-rendered
 * by the Section Rendering API on every cart change, so the numbers are always right without any
 * JS. This file does two things:
 *
 *   1. Animates the fill and the dot from where they were to where they now are, instead of
 *      letting them snap into place after a section swap.
 *   2. Refreshes the PDP mini-bar, which sits outside the mini-cart section and so is not covered
 *      by the theme's existing cart re-render. It refetches the Liquid-rendered section rather
 *      than recalculating, so the threshold never has to exist in JavaScript.
 */
class FreeShippingProgress extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const context = this.dataset.context || 'drawer';
    const ratio = parseFloat(this.dataset.ratio) || 0;
    const state = this.dataset.state;
    const previous = FreeShippingProgress.last[context];

    FreeShippingProgress.last[context] = { ratio: ratio, state: state };

    if (state === 'unlocked' && previous && previous.state !== 'unlocked') {
      this.classList.add('free-shipping-progress--just-unlocked');
    }

    if (this.prefersReducedMotion) return;
    if (!previous || previous.ratio === ratio) return;

    this.style.setProperty('--fs-ratio', previous.ratio);
    this.offsetWidth; // eslint-disable-line no-unused-expressions -- flush the start position

    requestAnimationFrame(() => {
      this.style.setProperty('--fs-ratio', ratio);
    });
  }

  get prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

// Tracked per context so the drawer bar and the PDP mini-bar do not consume each other's
// previous position and cancel one another's animation.
FreeShippingProgress.last = {};

customElements.define('free-shipping-progress', FreeShippingProgress);

/*
 * The PDP mini-bar. Subscribes to the theme's cart update event and swaps in a freshly rendered
 * copy of the free-shipping-bar section. The drawer deliberately does not do this — it is already
 * re-rendered by cart.js and product-form.js, so subscribing there would mean a second, redundant
 * request on every cart change.
 */
class FreeShippingBarPdp extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (typeof subscribe !== 'function') return;

    this.pending = false;
    this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.refresh.bind(this));
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
  }

  refresh() {
    if (this.pending) return;
    this.pending = true;

    const url = `${window.location.pathname}?section_id=${this.dataset.sectionId}`;

    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        const section = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('.shopify-section');

        this.innerHTML = section ? section.innerHTML : '';
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.pending = false;
      });
  }
}

customElements.define('free-shipping-bar-pdp', FreeShippingBarPdp);
